'use client';

import { useEffect } from 'react';
import { reportClientError } from '@/lib/clienterr';

// Deploys rename JS chunks, so a browser/PWA holding an old page (or mobile
// Safari failing a dynamic import) throws "ChunkLoadError" / "Importing a module
// script failed" → the page crashes with "application error". Detect that class
// of error and reload to pull the fresh bundle — but with a budget so a reload
// that keeps failing (e.g. a stubborn cache) can't spin into an infinite loop.
const CHUNK_RE =
  /ChunkLoadError|Loading chunk|Loading CSS chunk|Importing a module script failed|error loading dynamically imported module|Failed to fetch dynamically imported module/i;

// Reload at most twice within a 30s window; after 30s of stability the budget
// resets, so a future real deploy can recover again.
function recoverReload() {
  try {
    const KEY = 'wow:chunk-reload';
    const now = Date.now();
    const [c, t] = (sessionStorage.getItem(KEY) || '0:0').split(':');
    let count = Number(c) || 0;
    const ts = Number(t) || 0;
    if (now - ts > 30000) count = 0;
    if (count >= 2) return; // give up → the error boundary offers a manual reload
    sessionStorage.setItem(KEY, `${count + 1}:${now}`);
  } catch {
    /* private mode — reload anyway */
  }
  window.location.reload();
}

export default function ChunkGuard() {
  useEffect(() => {
    let beacons = 0; // cap raw-error reports so a noisy page can't flood the beacon
    const beacon = (src: string, e: unknown) => {
      if (beacons++ < 4) reportClientError(src, e);
    };
    const onError = (e: ErrorEvent) => {
      beacon('window-error', e.error ?? e.message);
      if (CHUNK_RE.test(e.message || '')) recoverReload();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      beacon('unhandledrejection', r);
      if (CHUNK_RE.test(String((r && r.message) || r || ''))) recoverReload();
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);
  return null;
}
